import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

export const SR_TABLE_THEME_DEFAULT = 'SR_TABLE_THEME_DEFAULT';
export const SR_TABLE_THEME_EXAMPLE = 'SR_TABLE_THEME_EXAMPLE';
export const SR_TABLE_THEME_SMALL = 'SR_TABLE_THEME_SMALL';
export const SR_TABLE_THEME_CLEAR = 'SR_TABLE_THEME_CLEAR';
export const SR_TABLE_THEME_HISTORICAL = 'SR_TABLE_THEME_HISTORICAL';
export const SR_TABLE_THEME_PARTNER = 'SR_TABLE_THEME_PARTNER';
export const SR_TABLE_THEME_PARTNER_CLEAR = 'SR_TABLE_THEME_PARTNER_CLEAR';

const SR_TABLE_THEME_TO_CLASSNAME_MAPPING = {
  [SR_TABLE_THEME_DEFAULT]: null,
  [SR_TABLE_THEME_EXAMPLE]: 'themeExample',
  [SR_TABLE_THEME_SMALL]: 'themeSmall',
  [SR_TABLE_THEME_CLEAR]: 'themeClear',
  [SR_TABLE_THEME_HISTORICAL]: 'themeHistorical',
  [SR_TABLE_THEME_PARTNER]: 'themePartner',
  [SR_TABLE_THEME_PARTNER_CLEAR]: 'themePartnerClear',
};

export const SR_TABLE_THEMES = Object.keys(SR_TABLE_THEME_TO_CLASSNAME_MAPPING);

/**
 * Wraps a table row with expander columns.
 *
 * @param  {Array}   row   Row to wrap with expander columns.
 * @param  {String}  size  The width of the expander columns.
 * @param  {String}  theme One of SR_TABLE_THEMES.
 * @return {Array}         New row with expander columns.
 */
function addExpanderColumns(row, size, theme) {
  if (!size) {
    return row;
  }

  const expanderCellClassName = classnames(
    'expanderCell',
    SR_TABLE_THEME_TO_CLASSNAME_MAPPING[theme],
  );

  return [
    { className: expanderCellClassName, style: { width: size } },
    ...row,
    { className: expanderCellClassName, style: { width: size } },
  ];
}

const SrTableHeaderCell = ({
  content,
  dataTableFieldId,
  onClick,
  className,
  noPadding,
  colSpan,
  theme,
  sort,
  sortPosition,
  filter,
  style,
}) => {
  const sortDOM = (sort || dataTableFieldId) && (
    <div className={classnames('tableHeadCellSort', sort && `tableHeadCellSort-${sort}`)} />
  );

  return (
    <th
      onClick={onClick}
      style={style}
      className={classnames(
        'tableHeadCell',
        className,
        noPadding && 'noPadding',
        onClick && 'cursor-pointer',
        SR_TABLE_THEME_TO_CLASSNAME_MAPPING[theme],
      )}
      colSpan={colSpan}
    >
      {sortPosition === 'left' && sortDOM}
      {content}
      {sortPosition === 'right' && sortDOM}
      {filter}
    </th>
  );
};

SrTableHeaderCell.defaultProps = {
  sortPosition: 'left',
};

SrTableHeaderCell.propTypes = {
  content: PropTypes.node,
  onClick: PropTypes.func,
  className: PropTypes.string,
  noPadding: PropTypes.bool,
  colSpan: PropTypes.number,
  theme: PropTypes.oneOf(SR_TABLE_THEMES).isRequired,
  style: PropTypes.object,
  sortPosition: PropTypes.string,
  filter: PropTypes.node,
};

const SrTableContentCell = ({
  content,
  onClick,
  className,
  colSpan,
  noPadding,
  overflowVisible,
  style,
  theme,
}) => {
  return (
    <td
      onClick={onClick}
      style={style}
      className={classnames(
        'tableBodyCell',
        className,
        SR_TABLE_THEME_TO_CLASSNAME_MAPPING[theme],
        noPadding && 'noPadding',
        overflowVisible && 'overflowVisible',
        onClick && 'clickable',
      )}
      colSpan={colSpan}
    >
      {content}
    </td>
  );
};

SrTableContentCell.propTypes = {
  content: PropTypes.node,
  onClick: PropTypes.func,
  className: PropTypes.string,
  colSpan: PropTypes.number,
  noPadding: PropTypes.bool,
  overflowVisible: PropTypes.bool,
  style: PropTypes.object,
  theme: PropTypes.oneOf(SR_TABLE_THEMES).isRequired,
};

const SrTableContentRow = ({
  onClick,
  className,
  cells,
  theme,
  sidePadding,
  rowIndex,
  hideRow,
  dataset,
}) => {
  const isZebra = rowIndex % 2 === 1;

  return hideRow ? (
    <div className="hiddenRow" />
  ) : (
    <tr
      className={classnames(
        'tableBodyRow',
        className,
        SR_TABLE_THEME_TO_CLASSNAME_MAPPING[theme],
        isZebra && 'tableBodyRowZebra',
        rowIndex === 0 && 'tableFirstRow',
        onClick && 'tableRowClickable',
      )}
      onClick={onClick}
      {...dataset}
    >
      {addExpanderColumns(cells, sidePadding, theme).map((contentCellProps, cellIndex) => (
        <SrTableContentCell
          {...contentCellProps}
          key={`cell-${cellIndex}`} // eslint-disable-line react/no-array-index-key
          theme={theme}
        />
      ))}
    </tr>
  );
};

SrTableContentRow.propTypes = {
  onClick: PropTypes.func,
  className: PropTypes.string,
  colSpan: PropTypes.number,
  cells: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.node,
      onClick: PropTypes.func,
      className: PropTypes.string,
      colSpan: PropTypes.number,
      style: PropTypes.object,
    }),
  ).isRequired,
  theme: PropTypes.oneOf(SR_TABLE_THEMES).isRequired,
  sidePadding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  rowIndex: PropTypes.number.isRequired,
  hideRow: PropTypes.bool,
  dataset: PropTypes.object,
};

SrTableContentRow.defaultProps = {
  hideRow: false,
};

const getColgroup = (headers) => {
  return headers.length > 0 ? (
    <colgroup>
      {headers.map((header, index) => (
        <col
          key={`table-col-${
            typeof header.content === 'string' && header.content
              ? header.content.replace(/\s/g, '-')
              : index
          }`}
          style={{ width: header.width > 0 ? `${header.width.toFixed(3)}%` : undefined }}
        />
      ))}
    </colgroup>
  ) : null;
};

const SrTable = ({ headers, rows, className, theme, sidePadding, minWidth, sticky }) => (
  <table
    className={classnames('SrTable', className, SR_TABLE_THEME_TO_CLASSNAME_MAPPING[theme])}
    style={minWidth ? { minWidth } : null}
  >
    {getColgroup(headers)}
    {!headers.length ? null : (
      <thead className={!sticky ? 'tableHead' : 'tableHead stickyTableHead'}>
        <tr className="tableHeadRow">
          {addExpanderColumns(headers, sidePadding, theme).map((headerCellProps, index) => (
            <SrTableHeaderCell
              {...headerCellProps}
              key={`header-${index}`} // eslint-disable-line react/no-array-index-key
              theme={theme}
            />
          ))}
        </tr>
      </thead>
    )}
    {!rows.length ? null : (
      <tbody
        className={classnames(
          !sticky ? 'tableBody' : 'tableBody stickyTableBody',
          SR_TABLE_THEME_TO_CLASSNAME_MAPPING[theme],
        )}
      >
        {rows.map((contentRowProps, rowIndex) => (
          <SrTableContentRow
            {...contentRowProps}
            key={`row-${rowIndex}`} // eslint-disable-line react/no-array-index-key
            theme={theme}
            sidePadding={sidePadding}
            rowIndex={rowIndex}
          />
        ))}
      </tbody>
    )}
  </table>
);

SrTable.displayName = 'SrTable';

SrTable.propTypes = {
  headers: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.node,
      onClick: PropTypes.func,
      className: PropTypes.string,
      style: PropTypes.object,
      sort: PropTypes.string,
      filter: PropTypes.node,
    }),
  ),
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      onClick: PropTypes.func,
      className: PropTypes.string,
      cells: PropTypes.arrayOf(
        PropTypes.shape({
          content: PropTypes.node,
          onClick: PropTypes.func,
          className: PropTypes.string,
          noPadding: PropTypes.bool,
          overflowVisible: PropTypes.bool,
          style: PropTypes.object,
        }),
      ).isRequired,
    }),
  ),
  theme: PropTypes.oneOf(SR_TABLE_THEMES),
  sidePadding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  minWidth: PropTypes.string,
  sticky: PropTypes.bool,
};

SrTable.defaultProps = {
  headers: [],
  rows: [],
  theme: SR_TABLE_THEME_DEFAULT,
};

SrTable.displayName = 'SrTable';

export default SrTable;
